import { Box, Center, Drawer, DrawerContent, DrawerOverlay, Flex } from '@chakra-ui/react';
import { UserRole } from '@prisma/client';
import React from 'react';
import { BrowserRouter as Router, Redirect, Route, Switch } from 'react-router-dom';
import { QueryParamProvider } from 'use-query-params';
import MainAppBar from './components/layout/app-bar/MainAppBar';
import MainMenu from './components/layout/menu/MainMenu';
import SpinnerCircle from './components/others/SpinnerCircle';
import BookingRequestsArchivePage from './pages/booking-requests/BookingRequestsArchivePage';
import { COLORS, DRAWER_WIDTH } from './root-constants';
import { useRoutes } from './routes';
import { useAuthState } from './state/auth';
import { useDrawer } from './state/drawer';
import UsersUtils from './utils/users.utils';

const PropertiesArchivePage = React.lazy(() => import('./pages/properties/PropertiesArchivePage'));
const PropertyPage = React.lazy(() => import('./pages/properties/property-page/PropertyPage'));

const BookingsArchivePage = React.lazy(() => import('./pages/bookings/BookingsArchivePage'));
const BookingPage = React.lazy(() => import('./pages/bookings/booking-page/BookingPage'));

const RoomsPage = React.lazy(() => import('./pages/rooms/RoomsPage'));
const RoomsArchivePage = React.lazy(() => import('./pages/rooms/archive/RoomsArchivePage'));

const GuestPage = React.lazy(() => import('./pages/guests/guest-page/GuestPage'));

const AuctionPage = React.lazy(() => import('./pages/auctions/auction-page/AuctionPage'));

const UserPage = React.lazy(() => import('./pages/users/user-page/UserPage'));

const CustomerPage = React.lazy(() => import('./pages/customers/customer-page/CustomerPage'));

const BookingRequestPage = React.lazy(
  () => import('./pages/booking-requests/booking-request-page/BookingRequestPage')
);

const App: React.FC = () => {
  const { user } = useAuthState();

  const routes = useRoutes();
  const { isOpen: isDrawerOpen, onClose: onCloseDrawer } = useDrawer();

  const PageSpinner = (
    <Center w="100%" h="100%" pb="150px">
      <SpinnerCircle size="lg" />
    </Center>
  );

  return (
    <Flex h="100%">
      <Router>
        <QueryParamProvider ReactRouterRoute={Route}>
          <MainAppBar />

          <Box display={{ base: 'none', lg: 'block' }} w="260px">
            <MainMenu routes={routes} />
          </Box>

          <Box display={{ base: 'block', lg: 'none' }}>
            <Drawer placement="left" isOpen={isDrawerOpen} onClose={onCloseDrawer}>
              <DrawerOverlay>
                <DrawerContent maxW="260px">
                  <MainMenu routes={routes} />
                </DrawerContent>
              </DrawerOverlay>
            </Drawer>
          </Box>

          <Box
            as="main"
            h="100%"
            flexGrow={1}
            bgColor={COLORS.BG}
            p="40px"
            maxWidth={{ base: '100%', lg: `calc(100% - ${DRAWER_WIDTH}px)` }}
          >
            <Box minH="54px" />

            <React.Suspense fallback={PageSpinner}>
              <Switch>
                {routes.map(({ exact, path, component }, index) => (
                  <Route exact={exact || false} path={path} component={component} key={index} />
                ))}

                <Route path="/properties/archive" component={PropertiesArchivePage} />
                <Route path="/bookings/archive" component={BookingsArchivePage} />
                <Route path="/booking-requests/archive" component={BookingRequestsArchivePage} />

                <Route path="/properties/:propertyId" component={PropertyPage} exact />
                <Route path="/properties/:propertyId/rooms" component={RoomsPage} exact />
                <Route
                  path={'/properties/:propertyId/rooms/archive'}
                  component={RoomsArchivePage}
                />

                <Route path="/bookings/:bookingId" component={BookingPage} />

                <Route path="/guests/:guestId" component={GuestPage} />

                <Route path="/auctions/:auctionId" component={AuctionPage} />

                <Route path="/users/:userId" component={UserPage} />

                <Route path="/customers/:customerId" component={CustomerPage} />

                <Route path="/booking-requests/:bookingRequestId" component={BookingRequestPage} />

                {!UsersUtils.hasRoles(user, UserRole.ADMIN) ? (
                  <Route exact path={'/'}>
                    <Redirect to={'/properties'} />
                  </Route>
                ) : null}

                <Route path="*">
                  <Redirect to="/" />
                </Route>
              </Switch>
            </React.Suspense>
          </Box>
        </QueryParamProvider>
      </Router>
    </Flex>
  );
};

export default App;
